import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

type UserRole = 'admin' | 'merchant' | 'user' | null;

export function useUserRole() {
  const [role, setRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    async function fetchUserRole() {
      if (!user) {
        setRole(null);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.log('No role found for user, defaulting to user role');
          setRole('user');
        } else {
          setRole(data.role as UserRole);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setRole('user');
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserRole();
  }, [user]);

  return { role, isLoading };
}

export function useRequireRole(requiredRole?: 'admin' | 'merchant') {
  const { user, isLoading: authLoading } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || roleLoading) return;
    
    if (!user) {
      navigate('/auth');
      return;
    }
    
    if (requiredRole && role !== requiredRole && role !== 'admin') {
      navigate('/');
      return;
    }
  }, [user, role, authLoading, roleLoading, requiredRole, navigate]);

  return { user, role, isLoading: authLoading || roleLoading };
}

export function useRequireAuth() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (!user) navigate('/auth');
  }, [user, isLoading, navigate]);

  return { user, isLoading };
}